import { useEffect, useRef, useState } from 'react';
import { useSpin } from '../context/SpinContext';
import { uploadImage, uploadAudio, createRender, pollRenderJob, downloadRenderBlob, downloadBlob } from '../lib/renderApi';

// Owns the export/render business logic (upload -> create job -> poll ->
// download) so ExportControls.jsx only has to render UI for whatever state
// this hook exposes.
export function useExportJob() {
  const { exportSettings, disc, background, foreground, audio } = useSpin();
  const [status, setStatus] = useState('idle'); // idle | exporting | done | error
  const [phaseLabel, setPhaseLabel] = useState('');
  const [pct, setPct] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const activeJobRef = useRef(null);

  const canExport = !!disc.imageEl && !!disc.file
    && (audio.noAudio ? audio.noAudioDuration > 0 : (audio.hasAudio && !!audio.file));

  useEffect(() => {
    function handleBeforeUnload(e) {
      if (status === 'exporting') {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);

  const handleExport = async () => {
    if (!canExport) return;
    setStatus('exporting');
    setErrorMsg(null);
    setPct(0);
    setPhaseLabel('Uploading artwork…');
    try {
      const discUpload = await uploadImage(disc.file, (f) => {
        setPct(Math.round(f * 100));
        setPhaseLabel(`Uploading disc art… ${Math.round(f * 100)}%`);
      });

      let backgroundImageId = null;
      if (background.type === 'image' && background.file) {
        const bgUpload = await uploadImage(background.file, (f) => {
          setPct(Math.round(f * 100));
          setPhaseLabel(`Uploading background… ${Math.round(f * 100)}%`);
        });
        backgroundImageId = bgUpload.file_id;
      }

      let foregroundImageId = null;
      if (foreground.file) {
        const fgUpload = await uploadImage(foreground.file, (f) => {
          setPct(Math.round(f * 100));
          setPhaseLabel(`Uploading logo… ${Math.round(f * 100)}%`);
        });
        foregroundImageId = fgUpload.file_id;
      }

      let audioId = null;
      if (!audio.noAudio) {
        const audioUpload = await uploadAudio(audio.file, (f) => {
          setPct(Math.round(f * 100));
          setPhaseLabel(`Uploading audio… ${Math.round(f * 100)}%`);
        });
        audioId = audioUpload.file_id;
      }

      setPct(0);
      setPhaseLabel('Starting render…');
      const { job_id } = await createRender({
        discImageId: discUpload.file_id,
        audioId,
        noAudio: audio.noAudio,
        durationSeconds: audio.noAudio ? audio.noAudioDuration : undefined,
        designMode: disc.designMode,
        shape: disc.shape,
        vinylColor: disc.vinyl.color,
        vinylSecondaryColor: disc.vinyl.secondaryColor,
        vinylFinish: disc.vinyl.finish,
        vinylSeed: disc.vinyl.seed,
        spindleVisible: disc.spindleVisible,
        centerHoleEnabled: disc.centerHole.enabled,
        centerHoleSizePct: disc.centerHole.sizePct,
        centerHoleFill: disc.centerHole.fill,
        centerHoleColor: disc.centerHole.color,
        size: disc.size,
        zoom: disc.zoom,
        rpm: disc.reverse ? -disc.rpm : disc.rpm,
        backgroundType: background.type,
        backgroundColor: background.color,
        backgroundImageId,
        foregroundImageId,
        foregroundScale: foreground.scale,
        trimStart: audio.noAudio ? 0 : audio.trimStart,
        trimEnd: audio.noAudio ? undefined : audio.trimEnd,
        fadeIn: audio.noAudio ? 0 : audio.fadeIn,
        fadeOut: audio.noAudio ? 0 : audio.fadeOut,
        aspect: exportSettings.aspect,
      });
      activeJobRef.current = job_id;

      const finalStatus = await pollRenderJob(job_id, {
        onProgress: (s) => {
          setPct(s.progress || 0);
          setPhaseLabel(s.phase || 'Working…');
        },
      });

      if (finalStatus.status === 'error') {
        throw new Error(finalStatus.error || 'Render failed. Please try again.');
      }

      setPhaseLabel('Downloading…');
      const blob = await downloadRenderBlob(job_id);
      downloadBlob(blob, 'waxify-video.mp4');
      setStatus('done');
    } catch (e) {
      setErrorMsg(e.message || 'Export failed. Please try again.');
      setStatus('error');
    }
  };

  return { status, phaseLabel, pct, errorMsg, canExport, handleExport };
}
